import { getObject, setObject } from "@/utils/storage-helpers";
import { KYC_ID_STATUS } from "./types";
import type { KycIdStatus, UsKycProfile } from "./types";

/**
 * 假数据。后端三套接口（geo / KYC / AMOE）目前一个都不存在，这里先写死，
 * 让整条交互链路可以完整跑起来；**后续接真接口时整个文件删掉**，
 * 连同 api/ 下三个文件里对它的引用一起换成真实请求。
 *
 * 要看不同分支就改下面这几个常量，不需要环境变量、不需要重配什么东西。
 * 唯独地理定位是真的：见 geolocation.ts。
 */
export const FAKE = {
  /**
   * 服务端对定位的判定结果。
   * false = 坐标拿到了但不在允许区域 → 进游戏弹地理围栏弹窗。
   *
   * 这是目前**唯一不依赖浏览器权限**就能看到地理围栏弹窗的开关，
   * 想验证那个弹窗就改这里。
   */
  geoCompliant: true,

  /**
   * 【临时】用自绘的底部弹窗代替浏览器那个系统定位权限框。
   *
   * true  = 弹自己画的那个（样式可控、可反复触发，见 permissionPrompt.ts），
   *         用户选「允许」就当拿到坐标，选「禁止」就当被拒
   * false = 老老实实调 navigator.geolocation，弹系统框
   *
   * ⚠️ 接真实合规时必须改回 false —— 自绘弹窗拿不到真实坐标，
   *    它只是个样式占位。真流程里它应该退化成「系统框之前的预询问」。
   */
  geoUseMockPermissionSheet: true,

  /**
   * 【临时】用户在地理围栏弹窗里点「Enable Location」时，是否直接当作定位成功。
   *
   * 只对**用户主动点按钮重试**这一次生效，登录时和点游戏时的自动检查不受影响 ——
   * 所以拦截该弹照弹，只是弹窗里那个按钮点下去一定能过，不会卡死。
   *
   * true  = 点按钮就当定位拿到了（合不合规仍由 geoCompliant 决定）
   * false = 按真实结果走：浏览器不给就还是 denied，弹窗留着并提示去改浏览器设置
   *
   * ⚠️ 接真实合规之前必须改回 false，并连同 geolocation.ts 里那段兜底一起删。
   */
  geoGrantOnRetry: true,
  /** 身份认证状态，取值见 types.ts 的 KYC_ID_STATUS */
  kycId: KYC_ID_STATUS.NOT_STARTED as KycIdStatus,
  /** 地址证明是否已过。0 时点 Verify Now 会先要求填 INFO 表单 */
  kycPoa: 0,
} as const;

/**
 * 「已经验证通过」的记忆。
 *
 * 用 utils 那套 storage：logout() 里的 _clearObject() 会把它清掉（它不在
 * keepDataKeys），而刷新页面不会。于是「刷新不再弹 KYC、退出后重新登录才再走
 * 一遍」这件事不用额外判断「这次是登录还是刷新」—— 与定位授权同一套路数
 * （见 permissionPrompt.ts）。
 *
 * 真接口接上后这段一起删 —— 那时状态是供应商回调写进后端的，前端不该有写入口。
 */
const STORAGE_KEY = "compliance-kyc-approved";

export const markFakeKycApproved = () => {
  setObject(STORAGE_KEY, "1");
};

export const getFakeKycId = (): KycIdStatus =>
  getObject(STORAGE_KEY) === "1" ? KYC_ID_STATUS.APPROVED : FAKE.kycId;

/** 网络延迟的假象：没有它，弹窗的 loading 态一闪而过，看不出点没点动。 */
export const fakeDelay = <T>(value: T, ms = 300): Promise<T> =>
  new Promise(resolve => setTimeout(() => resolve(value), ms));

/**
 * 上次提交的 INFO 资料。
 *
 * 用途：用户在「审核中」点 Re-verify 回到表单时把原内容回填 —— 让他改一处，
 * 而不是从头重填七个字段。
 *
 * 与通过状态同一套存储：logout 会清掉，刷新不会。
 * 真接口接上后这段删掉 —— 那时已提交的资料由后端返回（合规文档里的
 * getApplicantIdInfo / decision 那条），前端不该自己留一份。
 */
const PROFILE_KEY = "compliance-kyc-profile";

export const saveFakeKycProfile = (profile: UsKycProfile): void => {
  setObject(PROFILE_KEY, profile);
};

export const readFakeKycProfile = (): UsKycProfile | null =>
  (getObject(PROFILE_KEY) as UsKycProfile | null) || null;
